<template>
  <div v-if="article" class="max-w-6xl mx-auto px-4 py-8">
    <!-- Back button -->
    <button @click="goBack" class="flex items-center text-green-600 hover:text-green-800 mb-6">
  <i class="fas fa-arrow-left mr-2"></i> Back to all articles
</button>

    <!-- Repository-style Layout -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 bg-white p-6 rounded-xl shadow mb-10">
      <!-- Left Column: Document -->
      <div class="space-y-4">
        <!-- Cover -->
        <img
          :src="article.coverImage || 'https://www.minsu.edu.ph/template/images/logo.png'"
          alt="Document Cover"
          class="w-full h-auto rounded border"
        />

        <!-- Download Links -->
        <div>
          <h4 class="text-md font-semibold text-gray-800 mb-2">Open/Download</h4>
          <div v-if="article.openAccess">
            <div class="space-y-2">
              <a
                v-if="article.documentUrl"
                :href="article.documentUrl"
                target="_blank"
                class="flex items-center text-sm text-red-700 hover:underline"
              >
                <i class="fas fa-file-pdf mr-2 text-lg"></i>
                {{ article.title?.substring(0, 50) + '.pdf' }}
              </a>
              <a
                v-if="article.supplementalUrl"
                :href="article.supplementalUrl"
                target="_blank"
                class="flex items-center text-sm text-red-700 hover:underline"
              >
                <i class="fas fa-file-pdf mr-2 text-lg"></i>
                Supplemental File
              </a>
            </div>
          </div>
        </div>

        <!-- Download Abstract PDF -->
        <button
          @click="downloadPDF"
          class="w-full inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition"
        >
          <i class="fas fa-download mr-2"></i> Download Abstract PDF
        </button>

        <!-- Metadata -->
        <div class="mt-4 text-sm text-gray-600">
          <p><strong>Access note:</strong> {{ article.openAccess ? 'Open Access' : 'Restricted' }}</p>
          <p><strong>Publication date:</strong> {{ formatDate(article.publishedDate) }}</p>
        </div>
      </div>

      <!-- Right Column: Details -->
      <div class="md:col-span-2 space-y-4">
        <!-- Title -->
        <h1 class="text-2xl font-bold text-gray-900 leading-snug">
          {{ article.title ?? 'Untitled Article' }}
        </h1>

        <!-- Authors -->
        <div v-if="article.authors?.length">
          <h3 class="font-semibold text-gray-700"><b>Author(s)</b></h3>
          <p class="text-gray-800">{{ article.authors.join('; ') }}</p>
        </div>

        <!-- Advisers -->
        <div v-if="article.advisers?.length">
          <h3 class="font-semibold text-gray-700"><b>Professor Advisor(s)</b></h3>
          <p class="text-gray-800">{{ article.advisers.join('; ') }}</p>
        </div>

        <!-- Abstract -->
        <div>
          <h3 class="font-semibold text-gray-700"><b>Abstract</b></h3>
          <p class="text-gray-700 text-justify whitespace-pre-line leading-relaxed">
            {{ article.abstract || 'No abstract available.' }}
          </p>
        </div>

        <!-- Identifier -->
        <div v-if="article.identifier" class="text-sm text-gray-600">
          <p>
            <strong>Identifier:</strong>
            <a :href="article.identifier" target="_blank" class="text-blue-600 hover:underline">
              {{ article.identifier }}
            </a>
          </p>
        </div>
      </div>
    </div>

    <!-- Tags / Keywords -->
    <div v-if="article.tags?.length">
      <h3 class="font-semibold text-gray-700">Keywords</h3>
      <div class="flex flex-wrap gap-2 mt-1">
        <span
          v-for="tag in article.tags"
          :key="tag"
          class="inline-block bg-gray-100 text-gray-800 text-sm px-3 py-1 rounded-full"
        >
          {{ tag }}
        </span>
      </div>
    </div>

    <!-- Hidden PDF Abstract -->
    <div
      ref="pdfContent"
      style="visibility: hidden; position: absolute; top: 0; left: 0; width: 700px; background: white; padding: 40px; font-family: Arial, sans-serif;"
    >
      <!-- Header with Logos -->
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 1px solid #ddd; padding-bottom: 15px;">
        <!-- Bagong Pilipinas Logo -->
        <div style="width: 80px;">
          <img src="@/assets/bp.png" style="width: 100%;" alt="BP Logo" />
        </div>

        <!-- Center Title -->
        <div style="text-align: center; flex-grow: 1;">
          <h1 style="margin: 0; font-size: 22px;">Mindoro State University</h1>
          <p style="margin: 0; font-size: 12px;">Alcate, Victoria, Oriental Mindoro, Philippines 5205</p>
          <h2 style="margin: 5px 0 0 0; font-size: 18px;">Research Abstract</h2>
        </div>

        <!-- MINSU Logo -->
        <div style="width: 80px;">
          <img src="@/assets/minsu.png" style="width: 100%;" alt="MINSU Logo" />
        </div>
      </div>

      <!-- Document Metadata -->
      <div style="margin-bottom: 15px;">
        <p style="font-size: 14px;"><strong>Title:</strong> {{ article.title }}</p>
        <p style="font-size: 14px;"><strong>Author(s):</strong> {{ article.authors?.join(', ') }}</p>
        <p style="font-size: 14px;"><strong>Category:</strong> {{ article.category }}</p>
        <p style="font-size: 14px;"><strong>Type:</strong> {{ article.type }}</p>
        <p style="font-size: 14px;"><strong>Tags:</strong> {{ article.tags?.join(', ') }}</p>
        <p style="font-size: 14px;"><strong>Open Access:</strong> {{ article.openAccess ? 'Yes' : 'No' }}</p>
      </div>

      <!-- Abstract Content -->
      <h3 style="margin: 20px 0 10px 0; font-size: 16px;">Abstract</h3>
      <div style="line-height: 1.5; font-size: 14px; text-align: justify; white-space: pre-wrap;">
        {{ article.abstract }}
      </div>
    </div>
  </div>

  <div v-else class="text-center text-gray-500 py-16">
    <i class="fas fa-exclamation-triangle text-xl mr-2"></i> Article not found.
  </div>
</template>


<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import html2pdf from 'html2pdf.js'
import { useHead } from '@vueuse/head'
import { useRoute, useRouter } from 'vue-router'
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore'
import { getApp } from 'firebase/app'

const route = useRoute()
const router = useRouter()
const pdfContent = ref(null)

const slug = route.params.slug

const article = ref(null)
const db = getFirestore(getApp())


const head = ref({
  title: 'Research Article',
  meta: [],
  script: []
})
const goBack = () => {
  router.push('/browse') 
}


watch(article, (newArticle) => {
  if (!newArticle) return
  
  head.value = {
    title: newArticle.title || 'Research Article',
    meta: [
      {
        name: 'description',
        content: newArticle.abstract || 'Read the full research article and abstract.'
      },
      {
        name: 'keywords',
        content: newArticle.tags?.join(', ') || 'research, article'
      },
      
      { name: 'citation_title', content: newArticle.title },
      ...(newArticle.authors || []).map(author => ({
        name: 'citation_author',
        content: author
      })),
      { name: 'citation_publication_date', content: newArticle.publishedDate?.split('T')[0] || '' },
      { name: 'citation_journal_title', content: 'Mindoro State University Research Repository' },
      { name: 'citation_abstract', content: newArticle.abstract || '' },
      { name: 'citation_keywords', content: newArticle.tags?.join(', ') || '' },
      {
        name: 'citation_pdf_url',
        content: newArticle.documentUrl || ''
      },
      
      { property: 'og:title', content: newArticle.title },
      { property: 'og:description', content: newArticle.abstract },
      { property: 'og:type', content: 'article' },
      {
        property: 'og:image',
        content: newArticle.coverImage || 'https://www.minsu.edu.ph/template/images/logo.png'
      },
      {
        property: 'og:url',
        content: typeof window !== 'undefined' ? window.location.href : ''
      },
      {
        property: 'og:article:author',
        content: newArticle.authors?.join(', ') || 'Mindoro State University'
      },
      {
        property: 'og:article:section',
        content: newArticle.category || 'Research'
      },
      
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: newArticle.title },
      { name: 'twitter:description', content: newArticle.abstract },
      {
        name: 'twitter:image',
        content: newArticle.coverImage || 'https://www.minsu.edu.ph/template/images/logo.png'
      }
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ScholarlyArticle",
          headline: newArticle.title || 'Untitled Research',
          description: newArticle.abstract || 'No abstract available.',
          datePublished: newArticle.publishedDate || new Date().toISOString(),
          keywords: newArticle.tags || [],
          author: newArticle.authors?.map(name => ({
            "@type": "Person",
            name
          })) || [],
          publisher: {
            "@type": "Organization",
            name: "Mindoro State University",
            logo: {
              "@type": "ImageObject",
              url: "https://www.minsu.edu.ph/template/images/logo.png"
            }
          },
          inLanguage: "en",
          url: typeof window !== 'undefined' ? window.location.href : ''
        })
      }
    ]
  }
}, { immediate: true })


useHead(head)

onMounted(async () => {
  const q = query(collection(db, 'articles'), where('slug', '==', slug))
  const snapshot = await getDocs(q)
  if (!snapshot.empty) {
    article.value = {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data()
    }
  } else {
    console.warn('No article found for slug:', slug)
  }
})




const downloadPDF = async () => {
  const element = pdfContent.value;
  if (!element) return;

  const originalStyles = {
    visibility: element.style.visibility,
    position: element.style.position,
    width: element.style.width
  };
  const originalHTML = element.innerHTML;

  element.style.visibility = 'visible';
  element.style.position = 'relative';
  element.style.width = '700px';

  try {
    const [minsuLogo, pilipinasLogo] = await Promise.all([
      getBase64FromUrl('https://www.minsu.edu.ph/template/images/logo.png'),
      getBase64FromUrl('https://caap.gov.ph/wp-content/uploads/2023/09/Bagong-Pilipinas-logo.png')
    ]);

    element.innerHTML = originalHTML
      .replace('https://www.minsu.edu.ph/template/images/logo.png', minsuLogo)
      .replace('https://caap.gov.ph/wp-content/uploads/2023/09/Bagong-Pilipinas-logo.png', pilipinasLogo);

    await new Promise(resolve => {
      const images = element.querySelectorAll('img');
      let loaded = 0;
      if (images.length === 0) return resolve();
      images.forEach(img => {
        img.onload = img.onerror = () => {
          loaded++;
          if (loaded === images.length) resolve();
        };
      });
    });

    const opt = {
      margin: [10, 10, 10, 10],
      filename: `${article.value?.title || 'abstract'}.pdf`.replace(/[^a-z0-9]/gi, '_') + '.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: true,
        width: 700
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    await html2pdf().set(opt).from(element).save();

  } catch (error) {
    console.error('PDF generation error:', error);
    alert('Failed to generate PDF. Please try again.');
  } finally {
    element.innerHTML = originalHTML;
    element.style.visibility = originalStyles.visibility;
    element.style.position = originalStyles.position;
    element.style.width = originalStyles.width;
  }
}

const getBase64FromUrl = async (url) => {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error('Network response was not ok');
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading image:', url, error);
    return '';
  }
}

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString.toDate();
  return date.toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });
}

const formatTimeAgo = (dateString) => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString.toDate();
  const diff = Math.floor((new Date() - date) / 1000);
  if (diff < 60) return `${diff} seconds ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  return `${Math.floor(diff / 86400)} days ago`;
}

const relatedArticles = computed(() => {
  if (!article.value) return []
  return props.allArticles?.filter(a =>
    a.id !== article.value.id &&
    a.tags?.some(tag => article.value.tags?.includes(tag))
  ).slice(0, 3)
})
</script>

<style scoped>
.whitespace-pre-line {
  white-space: pre-line;
}

/* Print-specific styles */
@media print {
  .no-print {
    display: none !important;
  }
}

/* PDF content styles (hidden on screen) */
.pdf-content {
  visibility: hidden;
  position: absolute;
  left: -9999px;
}
</style>
<style scoped>
.whitespace-pre-line {
  white-space: pre-line;
}
</style>
